// Parser for Discord trade alert messages
// Extracts structured trade signal data from formatted alert messages

/**
 * Parse C Analytics sweep/flow alerts.
 * Example: " NEW SWEEP — CRWD $215 Put | September 4, 2026 | 3 DTE ..."
 */
function parseCAnalyticsSweep(content) {
  if (!content || typeof content !== 'string') return null;

  if (!/(NEW SWEEP|C Analytics)/i.test(content)) return null;

  const headerMatch = content.match(
    /NEW\s+SWEEP\s*[-–—]\s*\$?([A-Z]{1,5})\s+\$(\d+(?:\.\d+)?)\s+(Put|Call)\s*\|\s*([A-Za-z]+\s+\d{1,2},\s*\d{4})\s*\|\s*(\d+)\s*DTE/i
  );
  const suggestedMatch = content.match(
    /SUGGESTED\s+TRADE:\s*\$?([A-Z]{1,5})\s+\$(\d+(?:\.\d+)?)\s+(Put|Call)\s*\|\s*([A-Za-z]+\s+\d{1,2},\s*\d{4})\s*\|\s*(\d+)\s*DTE/i
  );
  const match = headerMatch || suggestedMatch;
  if (!match) return null;

  const [, symbol, strikeRaw, typeRaw, expStr, dte] = match;
  const strike = parseFloat(strikeRaw);
  const type = typeRaw.toLowerCase();
  const sideLetter = type === 'call' ? 'C' : 'P';
  const isPut = type === 'put';

  const priceMatch = content.match(/bought\s*\|?\s*@\s*\$?(\d*\.?\d+)\s*\/\s*contract/i);
  const price = priceMatch ? parseFloat(priceMatch[1]) : null;

  const stopLossMatch = content.match(/stop\s*loss:\s*(-?\d+)%\s*[-→>]\s*\$?(\d*\.?\d+)/i);
  const stopLoss = stopLossMatch ? parseFloat(stopLossMatch[2]) : null;
  const stopLossPercent = stopLossMatch ? parseInt(stopLossMatch[1]) : null;

  const profitTargets = [];
  const targetRegex = /\+(\d+)%\s*[-→>]\s*\$?(\d*\.?\d+)\s*\|\s*Win\s+Rate:\s*(\d+)%/gi;
  let m;
  while ((m = targetRegex.exec(content)) !== null) {
    profitTargets.push({
      percent: parseInt(m[1]),
      price: parseFloat(m[2]),
      winRate: parseInt(m[3])
    });
  }

  let expiration = null;
  try {
    const d = new Date(expStr);
    if (!isNaN(d.getTime())) {
      expiration = d.toISOString().split('T')[0];
    }
  } catch (e) {}

  return {
    isTradeAlert: true,
    alertType: 'entry',
    tradeStyle: null,
    strategy: isPut ? 'Put' : 'Call',
    strategyType: null,
    symbol: symbol.toUpperCase(),
    shortStrike: strike,
    longStrike: null,
    strikes: [strike],
    expiration,
    price,
    priceLabel: 'Entry',
    priceUnit: null,
    stopLoss,
    target: null,
    result: null,
    notes: [],
    raw: content,
    action: 'buy_to_open',
    optionSide: type,
    instrumentType: 'option',
    assetType: 'OPTION',
    quantity: 1,
    dte: parseInt(dte),
    legs: [{ strike, side: sideLetter }],
    profitTargets,
    stopLossPercent
  };
}

/**
 * Parse a Discord message content to detect and extract trade alert info.
 * Supports credit spread (CCS/PCS) and single-leg option alert formats.
 *
 * @param {string} content - Raw Discord message content
 * @returns {object|null} Parsed trade alert or null if not a trade alert
 */
export function parseTradeAlert(content) {
  if (!content || typeof content !== 'string') return null;

  // Try C Analytics sweep/flow format first
  const cAnalyticsParsed = parseCAnalyticsSweep(content);
  if (cAnalyticsParsed) return cAnalyticsParsed;

  const text = content
    .replace(/<a?:\w+:\d+>/g, '') // Discord custom emoji tags
    .replace(/@(?=[a-zA-Z])\w+/g, '') // Discord mentions (@everyone, @here, @role) — keep @1.2 prices
    .replace(/\*/g, '')                // Bold markers
    .replace(/\n+/g, ' ')             // Newlines to spaces
    .replace(/\s+/g, ' ')             // Collapse whitespace
    .trim();
  if (!text) return null;

  // Quick check: must contain some trade-related keyword
  const hasTradeKeyword = /\b(alert|trade|entry|exit|position|spread|ccs|pcs|credit|debit|bought|sold|stop|target|result|sto|bto|stc|btc|cr|db)\b/i.test(text);
  if (!hasTradeKeyword) return null;

  const result = {
    isTradeAlert: false,
    alertType: null,       // 'entry' | 'exit' | 'stop' | 'target' | 'info'
    tradeStyle: null,      // 'day_trade' | 'swing_trade' | 'scalp'
    strategy: null,        // 'CCS' | 'PCS' | 'CDS' | 'PDS' | 'Call Credit Spread' | etc.
    strategyType: null,    // 'credit' | 'debit'
    symbol: null,          // SPX, SPY, etc.
    shortStrike: null,
    longStrike: null,
    strikes: [],           // array of strike prices
    expiration: null,      // '0DTE', '7/17', '7/17/25', etc.
    price: null,           // numeric price
    priceLabel: null,      // 'Entry' | 'Exit Price' | 'Stop' | 'Target'
    priceUnit: null,       // 'credit' | 'debit' | null
    stopLoss: null,
    target: null,
    result: null,          // win/loss/breakeven text
    notes: [],
    raw: content,
  };

  // ─── Inline spread format: SYMBOL EXPIRATION $STRIKE/STRIKE Put/Call Credit/Debit Spread PRICE ──
  // e.g., "SPXW 7/28 $7245/7290 Put Credit Spread .20-.25"
  //       "SPY 7/19 $440/445 Call Debit Spread 2.00"
  //       "SPX 0DTE $5500/5510 Put Credit Spread 1.50"
  const inlineSpreadMatch = text.match(
    /([A-Z]{1,5}W?)\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2}|0DTE|\d+DTE)\s+\$?(\d+(?:\.\d+)?)\/\$?(\d+(?:\.\d+)?)\s+(put|call)\s+(credit|debit)\s+spread(?:\s+(\d*\.?\d+)(?:\s*[-–]\s*(\d*\.?\d+))?)?/i
  );
  if (inlineSpreadMatch) {
    const [, sym, exp, strike1, strike2, sideRaw, creditDebit, price1, price2] = inlineSpreadMatch;
    const side = sideRaw.toLowerCase();
    const sideLetter = side === 'call' ? 'C' : 'P';
    const isCredit = creditDebit.toLowerCase() === 'credit';
    const s1 = parseFloat(strike1);
    const s2 = parseFloat(strike2);

    result.alertType = 'entry';
    result.action = isCredit ? 'sell_to_open' : 'buy_to_open';
    result.symbol = sym.toUpperCase();
    result.shortStrike = s1;
    result.longStrike = s2;
    result.strikes = [s1, s2];
    result.legs = [
      { strike: s1, side: sideLetter },
      { strike: s2, side: sideLetter },
    ];
    result.strategy = isCredit
      ? (side === 'put' ? 'PCS' : 'CCS')
      : (side === 'put' ? 'PDS' : 'CDS');
    result.strategyType = isCredit ? 'credit' : 'debit';
    result.optionSide = side;
    result.instrumentType = 'option';
    result.expiration = exp.toUpperCase();
    if (price1) {
      result.price = parseFloat(price1);
      result.priceLabel = 'Entry';
      result.priceUnit = isCredit ? 'credit' : 'debit';
      if (price2) {
        result.priceMax = parseFloat(price2);
      }
    }

    result.isTradeAlert = true;
    return result;
  }

  // ─── Try pipe-delimited compact format first ─────────────────────────────
  // Format: STO | CEG | 210P & 320C | 8/21 exp | 4.95 cr
  // Format: BTO | SPX | 5800C | 0DTE exp | 2.50 db
  // Format: STC | AAPL | 150C | 7/17 exp | 3.20
  // The header is only the first line — 'text' has newlines collapsed to
  // spaces, which would glue bullet notes onto the last pipe segment.
  const contentLines = content
    .replace(/<a?:\w+:\d+>/g, '')
    .replace(/@(?=[a-zA-Z])\w+/g, '')
    .replace(/\*/g, '')
    .split('\n')
    .map(l => l.trim());
  const headerLineIdx = contentLines.findIndex(l => l.length);
  const firstLine = headerLineIdx >= 0 ? contentLines[headerLineIdx] : '';
  const pipeMatch = firstLine.match(/^\s*(STO|BTO|STC|BTC|SELL|BUY|CLOSE)\s*\|\s*([A-Z]{1,5})\s*\|(.+)$/i);
  if (pipeMatch) {
    const action = pipeMatch[1].toUpperCase();
    result.symbol = pipeMatch[2].toUpperCase();
    const pipeParts = pipeMatch[3].split('|').map(s => s.trim()).filter(Boolean);
    const bodyNotes = contentLines.slice(headerLineIdx + 1).filter(Boolean);
    let strikesRaw = null;
    let priceRaw = null;
    let unitRaw = null;
    let expRaw = null;
    const remainingNotes = [];

    for (const part of pipeParts) {
      if (priceRaw == null) {
        const priceUnitMatch = part.match(/^\$?(\d*\.?\d+)\s*(cr|db|credit|debit)?$/i);
        if (priceUnitMatch) {
          priceRaw = priceUnitMatch[1];
          const u = (priceUnitMatch[2] || '').toLowerCase();
          if (u === 'cr' || u === 'credit') { unitRaw = 'credit'; result.strategyType = 'credit'; }
          else if (u === 'db' || u === 'debit') { unitRaw = 'debit'; result.strategyType = 'debit'; }
          continue;
        }
      }
      if (!expRaw) {
        // Accept "exp" as prefix or suffix: "exp 10/16" and "10/16 exp"
        const expCandidate = part.replace(/\bexp\.?\b/i, '').trim();
        const expPartMatch = expCandidate.match(/^(0DTE|\d+DTE|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})$/i);
        if (expPartMatch) {
          expRaw = expPartMatch[1].toUpperCase();
          continue;
        }
      }
      if (!strikesRaw && /\d/.test(part)) {
        strikesRaw = part;
        continue;
      }
      remainingNotes.push(part);
    }

    // Strip a trailing strategy abbreviation (e.g. "95/85 PCS" -> "95/85" + PCS)
    if (strikesRaw) {
      const abbrevStrip = strikesRaw.match(/^(.*?)\s*\b(CCS|PCS|CDS|PDS)\b\s*$/i);
      if (abbrevStrip) {
        strikesRaw = abbrevStrip[1].trim();
        const spreadStrategies = {
          CCS: { strategy: 'CCS', strategyType: 'credit', optionSide: 'call' },
          PCS: { strategy: 'PCS', strategyType: 'credit', optionSide: 'put' },
          CDS: { strategy: 'CDS', strategyType: 'debit', optionSide: 'call' },
          PDS: { strategy: 'PDS', strategyType: 'debit', optionSide: 'put' },
        };
        const strat = spreadStrategies[abbrevStrip[2].toUpperCase()];
        if (strat) {
          result.strategy = strat.strategy;
          result.strategyType = strat.strategyType;
          result.optionSide = strat.optionSide;
        }
      }
    }

    // Parse action
    if (action === 'STO' || action === 'SELL') { result.alertType = 'entry'; result.action = 'sell_to_open'; }
    else if (action === 'BTO' || action === 'BUY') { result.alertType = 'entry'; result.action = 'buy_to_open'; }
    else if (action === 'STC') { result.alertType = 'exit'; result.action = 'sell_to_close'; }
    else if (action === 'BTC' || action === 'CLOSE') { result.alertType = 'exit'; result.action = 'buy_to_close'; }

    // Parse price and unit
    if (priceRaw) {
      result.price = parseFloat(priceRaw.replace('$', ''));
      result.priceLabel = result.alertType === 'exit' ? 'Exit' : 'Entry';
    }
    if (unitRaw === 'cr' || unitRaw === 'credit') { result.priceUnit = 'credit'; result.strategyType = 'credit'; }
    else if (unitRaw === 'db' || unitRaw === 'debit') { result.priceUnit = 'debit'; result.strategyType = 'debit'; }

    // Parse strikes: formats like "210P & 320C", "210P/320C", "5800C", "7580/7590"
    if (strikesRaw) {
      const legMatches = strikesRaw.matchAll(/(\d+(?:\.\d+)?)\s*([CP])/gi);
      const legs = [...legMatches].map(m => ({ strike: parseFloat(m[1]), side: m[2].toUpperCase() }));

      if (legs.length >= 2) {
        result.strikes = legs.map(l => l.strike);
        result.shortStrike = legs[0].strike;
        result.longStrike = legs[1].strike;
        result.legs = legs;
        // Detect strategy from legs
        const hasCall = legs.some(l => l.side === 'C');
        const hasPut = legs.some(l => l.side === 'P');
        if (hasCall && hasPut) {
          // Could be iron condor, strangle, or straddle
          if (legs.length === 4) { result.strategy = 'Iron Condor'; result.strategyType = 'credit'; }
          else if (legs[0].strike === legs[1].strike) { result.strategy = 'Straddle'; }
          else { result.strategy = 'Strangle'; result.strategyType = 'credit'; }
        } else if (hasCall && !hasPut) {
          if (legs.length === 2) {
            result.strategy = legs[0].strike < legs[1].strike ? 'CDS' : 'CCS';
            if (result.strategy === 'CCS') result.strategyType = 'credit';
            else result.strategyType = 'debit';
          } else { result.strategy = 'Call Spread'; }
        } else if (hasPut && !hasCall) {
          if (legs.length === 2) {
            result.strategy = legs[0].strike > legs[1].strike ? 'PDS' : 'PCS';
            if (result.strategy === 'PCS') result.strategyType = 'credit';
            else result.strategyType = 'debit';
          } else { result.strategy = 'Put Spread'; }
        }
      } else if (legs.length === 1) {
        result.strikes = [legs[0].strike];
        result.shortStrike = legs[0].strike;
        result.strategy = legs[0].side === 'C' ? 'Call' : 'Put';
        result.optionSide = legs[0].side === 'C' ? 'call' : 'put';
      } else {
        // Try slash-separated strikes without side letters: 7580/7590
        const slashStrikes = strikesRaw.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
        if (slashStrikes) {
          result.shortStrike = parseFloat(slashStrikes[1]);
          result.longStrike = parseFloat(slashStrikes[2]);
          result.strikes = [result.shortStrike, result.longStrike];
        } else {
          // Single number
          const singleNum = strikesRaw.match(/(\d+(?:\.\d+)?)/);
          if (singleNum) {
            result.shortStrike = parseFloat(singleNum[1]);
            result.strikes = [result.shortStrike];
          }
        }
      }

      // Build legs if side was determined and strikes were parsed
      if (result.optionSide && result.strikes.length > 0) {
        const sideLetter = result.optionSide === 'call' ? 'C' : 'P';
        if (result.strikes.length === 2) {
          result.legs = [
            { strike: result.shortStrike, side: sideLetter },
            { strike: result.longStrike, side: sideLetter },
          ];
        } else {
          result.legs = [{ strike: result.shortStrike, side: sideLetter }];
        }
      }
    }

    // Parse expiration
    if (expRaw) {
      const expClean = expRaw.replace(/\bexp\b/i, '').trim();
      result.expiration = expClean.toUpperCase();
    }

    // Use remaining pipe parts as notes (e.g. POP, trim, stop); keep the
    // message body lines (bullets below the header) as notes too.
    result.notes = [...remainingNotes, ...bodyNotes];

    // Check for IV rank, earnings, sector in notes
    const ivRankMatch = text.match(/IV\s*Rank\s*(?:of\s*)?(\d+)/i);
    if (ivRankMatch) result.ivRank = parseInt(ivRankMatch[1]);

    const earningsMatch = text.match(/earnings\s*(?:will\s*be\s*released\s*)?(?:before|after)\s*(?:expiration)?/i);
    if (earningsMatch) result.hasEarnings = true;

    const sectorMatch = text.match(/in\s*the\s+(\w+)\s+sector/i);
    if (sectorMatch) result.sector = sectorMatch[1];

    result.isTradeAlert = true;
    return result;
  }

  // ─── Try inline BOUGHT/SOLD/ALL OUT format ────────────────────────────────
  // Formats:
  //   BOUGHT 07/09 SPY 745c @ 1.7 @everyone med size
  //   SOLD 1/4 07/09 SPY 745c @ 2.04 @everyone +20% SCALED
  //   ALL OUT 07/09 SPY 745c @ 2.35 @everyone out runners
  //   BOUGHT 0DTE SPX 6610p @ 2.0
  //   SOLD 1/2 SPX 670c 0DTE @ 2.3
  // Also handles missing spaces: BOUGHT 10/3SPX670c@1.0

  const inlinePatterns = [
    // BOUGHT [exp] [symbol] [strike][side] [qty?] @ [price]
    { re: /bought\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*(?:x\s*(\d+)|(\d+))?\s*@\s*(\d*\.?\d+|market|mkt)/i,
      action: 'buy_to_open', alertType: 'entry' },
    // BOUGHT [symbol] [strike][side] [exp] @ [price]  (symbol-first variant)
    { re: /bought\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s*(?:x\s*(\d+)|(\d+))?\s*@\s*(\d*\.?\d+|market|mkt)/i,
      action: 'buy_to_open', alertType: 'entry', symbolFirst: true },
    // SOLD [partial] [exp] [symbol] [strike][side] @ [price]?
    { re: /sold\s+(1\/[2-4]|half|quarter|\d+\/\d+)\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*(?:@\s*(\d*\.?\d+|market|mkt))?/i,
      action: 'sell_to_close', alertType: 'exit', hasPartial: true },
    // SOLD [partial] [symbol] [strike][side] [exp] @ [price]?  (symbol-first variant)
    { re: /sold\s+(1\/[2-4]|half|quarter|\d+\/\d+)\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s*(?:@\s*(\d*\.?\d+|market|mkt))?/i,
      action: 'sell_to_close', alertType: 'exit', hasPartial: true, symbolFirst: true },
    // ALL OUT [exp] [symbol] [strike][side] @ [price]
    { re: /all\s*out\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*@\s*(\d*\.?\d+|market|mkt)/i,
      action: 'sell_to_close', alertType: 'exit' },
    // ALL OUT [symbol] [strike][side] [exp] @ [price]  (symbol-first variant)
    { re: /all\s*out\s+([A-Z]{1,5})\s*(\d+(?:\.\d+)?)\s*([cp]|call|put)\s*(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s*@\s*(\d*\.?\d+|market|mkt)/i,
      action: 'sell_to_close', alertType: 'exit', symbolFirst: true },
  ];

  for (const pat of inlinePatterns) {
    const m = text.match(pat.re);
    if (m) {
      let exp, sym, strike, sideRaw, qty, priceRaw, partial;

      if (pat.symbolFirst) {
        // symbol-first: group order differs per pattern type
        if (pat.hasPartial) {
          partial = m[1]; sym = m[2]; strike = m[3]; sideRaw = m[4]; exp = m[5]; priceRaw = m[6];
        } else {
          sym = m[1]; strike = m[2]; sideRaw = m[3]; exp = m[4]; qty = m[5] || m[6]; priceRaw = m[7];
        }
      } else {
        if (pat.hasPartial) {
          partial = m[1]; exp = m[2]; sym = m[3]; strike = m[4]; sideRaw = m[5]; priceRaw = m[6];
        } else {
          exp = m[1]; sym = m[2]; strike = m[3]; sideRaw = m[4]; qty = m[5] || m[6]; priceRaw = m[7];
        }
      }

      const side = sideRaw.toLowerCase().startsWith('c') || sideRaw.toLowerCase().startsWith('call') ? 'call' : 'put';
      const sideLetter = side === 'call' ? 'C' : 'P';
      const isMarket = priceRaw && /^(market|mkt)$/i.test(priceRaw);

      result.action = pat.action;
      result.alertType = pat.alertType;
      result.symbol = sym.toUpperCase();
      result.shortStrike = parseFloat(strike);
      result.strikes = [result.shortStrike];
      result.legs = [{ strike: result.shortStrike, side: sideLetter }];
      result.optionSide = side;
      result.expiration = exp.toUpperCase();
      result.strategy = side === 'call' ? 'Call' : 'Put';
      result.instrumentType = 'option';

      if (priceRaw && !isMarket) {
        result.price = parseFloat(priceRaw);
        result.priceLabel = pat.alertType === 'exit' ? 'Exit' : 'Entry';
      } else if (isMarket) {
        result.priceLabel = 'Market';
      }

      if (partial) {
        result.partialQuantity = partial;
      }

      if (qty) {
        result.quantity = parseInt(qty);
      }

      // Extract notes: everything after the match, plus emoji/percentage annotations
      const afterMatch = text.slice(m.index + m[0].length).trim();
      if (afterMatch) {
        result.notes = afterMatch.split(/\n+/).map(l => l.trim()).filter(Boolean);
        // Check for percentage gain
        const pctMatch = afterMatch.match(/\+(\d+)%/);
        if (pctMatch) result.gainPercent = parseInt(pctMatch[1]);
        // Check for scaled/runner/BE/ITM keywords
        if (/scaled/i.test(afterMatch)) result.scaled = true;
        if (/runner/i.test(afterMatch)) result.hasRunners = true;
        if (/\bBE\b|\bbreakeven\b/i.test(afterMatch)) result.breakeven = true;
        if (/\bITM\b|\bin\s*the\s*money\b/i.test(afterMatch)) result.inTheMoney = true;
      }

      // Check for high-risk keywords
      if (/\b(lotto|yolo|high\s*risk|very\s*risky|small\s*size|tiny\s*size|med\s*size)\b/i.test(text)) {
        result.isHighRisk = /lotto|yolo|high\s*risk|very\s*risky/i.test(text);
        result.sizeNote = text.match(/\b(med\s*size|small\s*size|tiny\s*size|large\s*size)\b/i)?.[1];
      }

      result.isTradeAlert = true;
      return result;
    }
  }

  // ─── ENTRY/EXIT BUY/SELL TO OPEN/CLOSE format ───────────────────────────
  // Format: ENTRY BUY TO OPEN Put SPY 744 1DTE
  //         EXIT SELL TO CLOSE Call SPY 745 0DTE
  //         ENTRY SELL TO OPEN Call SPY 450 7/19
  const entryExitPattern = text.match(
    /^(entry|exit)\s+(buy|sell)\s+to\s+(open|close)\s+(call|put)\s+([A-Z]{1,5})\s+(\d+(?:\.\d+)?)\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s*(?:@?\s*(\$?\d*\.?\d+|market|mkt))?/i
  );
  if (entryExitPattern) {
    const [, alertTypeWord, buySell, openClose, sideRaw, sym, strike, exp, priceRaw] = entryExitPattern;
    const side = sideRaw.toLowerCase();
    const sideLetter = side === 'call' ? 'C' : 'P';
    const isOpen = openClose.toLowerCase() === 'open';
    const isBuy = buySell.toLowerCase() === 'buy';

    result.alertType = alertTypeWord.toLowerCase();
    result.action = `${isBuy ? 'buy' : 'sell'}_to_${isOpen ? 'open' : 'close'}`;
    result.symbol = sym.toUpperCase();
    result.shortStrike = parseFloat(strike);
    result.strikes = [result.shortStrike];
    result.legs = [{ strike: result.shortStrike, side: sideLetter }];
    result.optionSide = side;
    result.expiration = exp.toUpperCase();
    result.strategy = side === 'call' ? 'Call' : 'Put';
    result.instrumentType = 'option';

    if (priceRaw) {
      const isMarket = /^(market|mkt)$/i.test(priceRaw);
      if (!isMarket) {
        result.price = parseFloat(priceRaw.replace('$', ''));
        result.priceLabel = result.alertType === 'exit' ? 'Exit' : 'Entry';
      } else {
        result.priceLabel = 'Market';
      }
    }

    result.isTradeAlert = true;
    return result;
  }

  // ─── ENTRY/EXIT STRATEGY SYMBOL STRIKE/STRIKE format ─────────────────────
  // Format: ENTRY CCS SPX 7550/7560 1DTE @ 1.15
  //         EXIT PCS SPY 440/430 0DTE @ 0.50
  //         ENTRY CDS SPY 440/450 7/19 @ 2.00
  const spreadStrategies = {
    CCS: { strategy: 'CCS', strategyType: 'credit', optionSide: 'call', sides: ['C', 'C'] },
    PCS: { strategy: 'PCS', strategyType: 'credit', optionSide: 'put', sides: ['P', 'P'] },
    CDS: { strategy: 'CDS', strategyType: 'debit', optionSide: 'call', sides: ['C', 'C'] },
    PDS: { strategy: 'PDS', strategyType: 'debit', optionSide: 'put', sides: ['P', 'P'] },
  };
  const compactSpreadMatch = text.match(
    /^(entry|exit)\s+(CCS|PCS|CDS|PDS)\s+([A-Z]{1,5})\s+(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|0DTE|\d+DTE)\s*(?:@?\s*(\$?\d*\.?\d+|market|mkt))?/i
  );
  if (compactSpreadMatch) {
    const [, alertTypeWord, strategyRaw, sym, strike1, strike2, exp, priceRaw] = compactSpreadMatch;
    const strategyKey = strategyRaw.toUpperCase();
    const strat = spreadStrategies[strategyKey];
    if (strat) {
      const s1 = parseFloat(strike1);
      const s2 = parseFloat(strike2);
      result.alertType = alertTypeWord.toLowerCase();
      result.action = alertTypeWord.toLowerCase() === 'entry' ? 'sell_to_open' : 'buy_to_close';
      result.symbol = sym.toUpperCase();
      result.shortStrike = s1;
      result.longStrike = s2;
      result.strikes = [s1, s2];
      result.legs = [
        { strike: s1, side: strat.sides[0] },
        { strike: s2, side: strat.sides[1] },
      ];
      result.strategy = strat.strategy;
      result.strategyType = strat.strategyType;
      result.optionSide = strat.optionSide;
      result.instrumentType = 'option';
      result.expiration = exp.toUpperCase();

      if (priceRaw) {
        const isMarket = /^(market|mkt)$/i.test(priceRaw);
        if (!isMarket) {
          result.price = parseFloat(priceRaw.replace('$', ''));
          result.priceLabel = result.alertType === 'exit' ? 'Exit' : 'Entry';
        } else {
          result.priceLabel = 'Market';
        }
      }

      result.isTradeAlert = true;
      return result;
    }
  }

  // ─── VERB-first spread format ──────────────────────────────────────────
  // Same shape as the single-leg "BOUGHT 0DTE SPX 7600c @ 2.34" alerts, but
  // with a strategy abbreviation and two strikes:
  //   BOUGHT 0DTE SPX PCS 7600/7590 @1.2
  //   SOLD 1/2 SPX PCS 7600/7590 @0.6
  //   ALL OUT SPX PCS 7600/7590 @ market
  // Convention (same as ENTRY/EXIT format): first strike is the short/primary leg.
  const verbSpreadMatch = text.match(
    /^\s*(bought|sold|buy|sell|opened|closed|covered|short|all\s*out|allout|bto|sto|btc|stc)\s+(?:(\d)\s*\/\s*(\d)\s+)?(?:(0DTE|\d+DTE|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s+)?([A-Z]{1,5}W?)\s+(CCS|PCS|CDS|PDS)\s+\$?(\d+(?:\.\d+)?)\s*\/\s*\$?(\d+(?:\.\d+)?)\s*(?:@?\s*(\$?\d*\.?\d+|market|mkt))?/i
  );
  if (verbSpreadMatch) {
    const [, verb, fracN, fracD, exp, sym, strategyRaw, strike1, strike2, priceRaw] = verbSpreadMatch;
    const strat = spreadStrategies[strategyRaw.toUpperCase()];
    if (strat) {
      const s1 = parseFloat(strike1);
      const s2 = parseFloat(strike2);
      const isOpenVerb = /^(bought|buy|opened|short|bto|sto)$/i.test(verb);
      const isCredit = strat.strategyType === 'credit';
      result.alertType = isOpenVerb ? 'entry' : 'exit';
      result.action = isOpenVerb
        ? (isCredit ? 'sell_to_open' : 'buy_to_open')
        : (isCredit ? 'buy_to_close' : 'sell_to_close');
      if (/^all\s*out$|^allout$/i.test(verb)) result.allOut = true;
      result.symbol = sym.toUpperCase();
      result.shortStrike = s1;
      result.longStrike = s2;
      result.strikes = [s1, s2];
      result.legs = [
        { strike: s1, side: strat.sides[0] },
        { strike: s2, side: strat.sides[1] },
      ];
      result.strategy = strat.strategy;
      result.strategyType = strat.strategyType;
      result.optionSide = strat.optionSide;
      result.instrumentType = 'option';
      if (exp) result.expiration = exp.toUpperCase();
      if (fracN && fracD) result.partialQuantity = `${fracN}/${fracD}`;

      if (priceRaw) {
        const isMarket = /^(market|mkt)$/i.test(priceRaw);
        if (!isMarket) {
          result.price = parseFloat(priceRaw.replace('$', ''));
          result.priceLabel = result.alertType === 'exit' ? 'Exit' : 'Entry';
        } else {
          result.priceLabel = 'Market';
        }
      }

      result.isTradeAlert = true;
      return result;
    }
  }

  // ─── Detect alert type and trade style ────────────────────────────────────

  const alertTypeMatch = text.match(/\b(day\s*trade|swing\s*trade|scalp)\s*(alert|entry|exit|stop|target)?\b/i);
  if (alertTypeMatch) {
    const style = alertTypeMatch[1].toLowerCase().replace(/\s+/g, ' ');
    result.tradeStyle = style.includes('day') ? 'day_trade' : style.includes('swing') ? 'swing_trade' : 'scalp';

    const subType = (alertTypeMatch[2] || '').toLowerCase().trim();
    if (subType === 'entry' || subType === 'alert') result.alertType = 'entry';
    else if (subType === 'exit') result.alertType = 'exit';
    else if (subType === 'stop') result.alertType = 'stop';
    else if (subType === 'target') result.alertType = 'target';
  }

  // Also detect standalone Entry/Exit keywords
  if (!result.alertType) {
    if (/\bentry\b/i.test(text) && !/\bexit\b/i.test(text)) result.alertType = 'entry';
    else if (/\bexit\b/i.test(text)) result.alertType = 'exit';
  }

  // ─── Detect strategy (spread type) ────────────────────────────────────────

  const strategyPatterns = [
    { re: /call\s*credit\s*spread\s*\(?\s*(ccs)\s*\)?/i, strategy: 'CCS', strategyType: 'credit', optionSide: 'call' },
    { re: /put\s*credit\s*spread\s*\(?\s*(pcs)\s*\)?/i, strategy: 'PCS', strategyType: 'credit', optionSide: 'put' },
    { re: /call\s*debit\s*spread\s*\(?\s*(cds)\s*\)?/i, strategy: 'CDS', strategyType: 'debit', optionSide: 'call' },
    { re: /put\s*debit\s*spread\s*\(?\s*(pds)\s*\)?/i, strategy: 'PDS', strategyType: 'debit', optionSide: 'put' },
    { re: /\b(ccs)\b/i, strategy: 'CCS', strategyType: 'credit', optionSide: 'call' },
    { re: /\b(pcs)\b/i, strategy: 'PCS', strategyType: 'credit', optionSide: 'put' },
    { re: /\b(cds)\b/i, strategy: 'CDS', strategyType: 'debit', optionSide: 'call' },
    { re: /\b(pds)\b/i, strategy: 'PDS', strategyType: 'debit', optionSide: 'put' },
    { re: /iron\s*condor/i, strategy: 'Iron Condor', strategyType: 'credit', optionSide: null },
    { re: /iron\s*butterfly/i, strategy: 'Iron Butterfly', strategyType: 'credit', optionSide: null },
    { re: /strangle/i, strategy: 'Strangle', strategyType: null, optionSide: null },
    { re: /straddle/i, strategy: 'Straddle', strategyType: null, optionSide: null },
    { re: /single\s*leg|naked|cash\s*secured\s*put|csp/i, strategy: 'Single Leg', strategyType: null, optionSide: null },
  ];

  for (const { re, strategy, strategyType, optionSide } of strategyPatterns) {
    if (re.test(text)) {
      result.strategy = strategy;
      result.strategyType = strategyType;
      result.optionSide = optionSide;
      break;
    }
  }

  // ─── Extract Position line ────────────────────────────────────────────────
  // Formats:
  //   Position: SPX CCS 7580/7590 0DTE
  //   Position: SPX CCS  7580/7590 0DTE
  //   Position: SPY 500 Call 7/17
  //   Position: SPX 5800 Put 0DTE

  const positionMatch = text.match(/position\s*:?\s*([A-Z]{1,5})\s+([A-Z]{2,4})?\s*(\d+(?:\.\d+)?)\s*[\/\s]\s*(\d+(?:\.\d+)?)?\s*(0DTE|\d+DTE|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})?/i);
  const positionMatch2 = text.match(/position\s*:?\s*([A-Z]{1,5})\s+([A-Z]{2,4})?\s*(\d+(?:\.\d+)?)\s*[\/\s]\s*(\d+(?:\.\d+)?)\s*(0DTE|\d+DTE|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})?/i);

  // Try a more flexible position parser
  const posLine = text.match(/position\s*:?\s*(.+)/i);
  if (posLine) {
    const posText = posLine[1].trim();
    // Extract symbol (first uppercase word, 1-5 chars)
    const symMatch = posText.match(/^([A-Z]{1,5})\b/);
    if (symMatch) result.symbol = symMatch[1].toUpperCase();

    // Extract strikes (numbers separated by /)
    const strikesMatch = posText.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (strikesMatch) {
      result.shortStrike = parseFloat(strikesMatch[1]);
      result.longStrike = parseFloat(strikesMatch[2]);
      result.strikes = [result.shortStrike, result.longStrike];
    } else {
      // Single strike
      const singleStrike = posText.match(/\b(\d+(?:\.\d+)?)\b/);
      if (singleStrike) {
        result.shortStrike = parseFloat(singleStrike[1]);
        result.strikes = [result.shortStrike];
      }
    }

    // Extract expiration
    const expMatch = posText.match(/\b(0DTE|\d+DTE|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})\b/i);
    if (expMatch) result.expiration = expMatch[1].toUpperCase();
  }

  // ─── Extract Entry / Exit Price ───────────────────────────────────────────
  // Formats:
  //   Entry: 1.10 credit
  //   Exit Price: 0.45 (or best available)
  //   Entry: 1.10 debit

  const entryMatch = text.match(/entry\s*:?\s*(\$?\d*\.?\d+)\s*(credit|debit)?/i);
  if (entryMatch) {
    result.price = parseFloat(entryMatch[1].replace('$', ''));
    result.priceLabel = 'Entry';
    result.priceUnit = entryMatch[2] ? entryMatch[2].toLowerCase() : null;
  }

  const exitMatch = text.match(/exit\s*price\s*:?\s*(\$?\d*\.?\d+)/i);
  if (exitMatch) {
    result.price = parseFloat(exitMatch[1].replace('$', ''));
    result.priceLabel = 'Exit Price';
    result.alertType = result.alertType || 'exit';
  }

  // Also check for "Exit" without "Price"
  if (!result.price) {
    const exitOnlyMatch = text.match(/exit\s*:?\s*(\$?\d*\.?\d+)/i);
    if (exitOnlyMatch) {
      result.price = parseFloat(exitOnlyMatch[1].replace('$', ''));
      result.priceLabel = 'Exit';
      result.alertType = result.alertType || 'exit';
    }
  }

  // ─── Extract Stop Loss ────────────────────────────────────────────────────
  const stopMatch = text.match(/stop\s*(?:loss)?\s*:?\s*(\$?\d*\.?\d+)/i);
  if (stopMatch) {
    result.stopLoss = parseFloat(stopMatch[1].replace('$', ''));
    if (!result.alertType) result.alertType = 'stop';
  }

  // ─── Extract Target ───────────────────────────────────────────────────────
  const targetMatch = text.match(/target\s*:?\s*(\$?\d*\.?\d+)/i);
  if (targetMatch) {
    result.target = parseFloat(targetMatch[1].replace('$', ''));
    if (!result.alertType) result.alertType = 'target';
  }

  // ─── Extract Result ───────────────────────────────────────────────────────
  const resultMatch = text.match(/result\s*:?\s*(.+)/i);
  if (resultMatch) {
    result.result = resultMatch[1].trim().slice(0, 200);
  }

  // ─── Generic header fallback: SYMBOL STRIKE[/STRIKE] EXPIRATION ─────────
  // Catches headers without an explicit "Position:" label, e.g.:
  //   "PCS 0DTE - SPX 7585/7575 9/10/26"
  //   "Day Trade Alert: SPY 580 9/10/26"
  if (!result.symbol) {
    const headerMatch = text.match(
      /\b([A-Z]{1,5}W?)\s+(\d+(?:\.\d+)?)(?:\s*\/\s*(\d+(?:\.\d+)?))?\s+(0DTE|\d+DTE|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})\b/
    );
    if (headerMatch) {
      const [, sym, strike1, strike2, exp] = headerMatch;
      result.symbol = sym.toUpperCase();
      result.shortStrike = parseFloat(strike1);
      result.strikes = strike2 ? [result.shortStrike, parseFloat(strike2)] : [result.shortStrike];
      if (strike2) result.longStrike = parseFloat(strike2);
      result.expiration = exp.toUpperCase();
    }
  }

  // ─── Determine if this is a trade alert ───────────────────────────────────
  // Must have at least a symbol and some trade structure
  const hasStructure = result.strategy || result.strikes.length > 0 || result.price !== null || result.alertType;
  result.isTradeAlert = !!(result.symbol && hasStructure);

  if (!result.isTradeAlert) return null;

  // Derive action and legs for auto-trading if not already set
  if (!result.action && result.alertType && result.strategy) {
    const isOpen = result.alertType === 'entry';
    const isCredit = result.strategyType === 'credit';
    const optionSideLetter = result.optionSide === 'call' ? 'C' : 'P';

    if (result.strikes.length === 2 && result.shortStrike != null && result.longStrike != null) {
      if (isCredit) {
        result.action = isOpen ? 'sell_to_open' : 'buy_to_close';
      } else {
        result.action = isOpen ? 'buy_to_open' : 'sell_to_close';
      }
      result.legs = [
        { strike: result.shortStrike, side: optionSideLetter },
        { strike: result.longStrike, side: optionSideLetter },
      ];
    } else if (result.strikes.length === 1 && result.shortStrike != null) {
      if (isCredit) {
        result.action = isOpen ? 'sell_to_open' : 'buy_to_close';
      } else {
        result.action = isOpen ? 'buy_to_open' : 'sell_to_close';
      }
      result.legs = [{ strike: result.shortStrike, side: optionSideLetter }];
    }
  }

  return result;
}

/**
 * Get a color for the alert type for UI display.
 * @param {string} alertType
 * @returns {string} MUI color name
 */
export function getAlertColor(alertType) {
  switch (alertType) {
    case 'entry': return 'success';
    case 'exit': return 'info';
    case 'stop': return 'error';
    case 'target': return 'warning';
    default: return 'primary';
  }
}

/**
 * Format the parsed alert into a human-readable summary line.
 * @param {object} alert - Parsed trade alert
 * @returns {string} Summary string
 */
export function formatAlertSummary(alert) {
  if (!alert || !alert.isTradeAlert) return '';

  const parts = [];
  if (alert.alertType) parts.push(alert.alertType.toUpperCase());
  if (alert.action) parts.push(alert.action.replace(/_/g, ' ').toUpperCase());
  if (alert.strategy) parts.push(alert.strategy);
  if (alert.symbol) parts.push(alert.symbol);
  if (alert.strikes.length >= 2) {
    if (alert.legs && alert.legs.length >= 2) {
      parts.push(alert.legs.map(l => `${l.strike}${l.side}`).join(' & '));
    } else {
      parts.push(`${alert.shortStrike}/${alert.longStrike}`);
    }
  } else if (alert.strikes.length === 1) {
    parts.push(`${alert.shortStrike}`);
  }
  if (alert.expiration) parts.push(alert.expiration);
  if (alert.price !== null) {
    const unit = alert.priceUnit ? ` ${alert.priceUnit}` : '';
    parts.push(`@ ${alert.price}${unit}`);
  }

  return parts.join(' ');
}
